import { useEffect, useState } from "react";
import {
  CheckCircle2, CircleSlash, FileQuestion, Inbox, Lock, RefreshCw, TriangleAlert, WifiOff, Wrench,
} from "lucide-react";

/* Tous les états d'écran de la console. Un état = une icône sobre, une phrase
   qui dit quoi faire, et rien d'autre. */

function State({ tone = "", icon: Icon, title, text, actions, meta, inline, testid }) {
  return (
    <div className={`adm-state${tone ? ` is-${tone}` : ""}${inline ? " is-inline" : ""}`} role="status" data-testid={testid}>
      {Icon && <span className="adm-state-icon"><Icon size={17} /></span>}
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {actions && <div className="adm-state-actions">{actions}</div>}
      {meta && <div className="adm-state-meta">{meta}</div>}
    </div>
  );
}

export function Loading({ label = "Chargement…", inline = false }) {
  if (inline) {
    return (
      <span className="adm-loading" role="status" aria-live="polite" data-testid="state-loading">
        <span className="adm-spinner" aria-hidden="true" />{label}
      </span>
    );
  }
  return (
    <div className="adm-state" role="status" aria-live="polite" data-testid="state-loading">
      <span className="adm-spinner" aria-hidden="true" />
      <p style={{ marginTop: 10 }}>{label}</p>
    </div>
  );
}

// Barres grises de remplacement. `rows` > 1 empile des lignes de largeur variée.
export function Skeleton({ width = "100%", height = 9, rows = 1, gap = 8, className = "" }) {
  const widths = ["100%", "84%", "92%", "72%", "88%"];
  return (
    <span aria-hidden="true" className={className} style={{ display: "grid", gap }}>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="adm-sk" style={{ width: rows > 1 ? widths[i % widths.length] : width, height }} />
      ))}
    </span>
  );
}

export function Empty({
  title = "Aucun résultat",
  text = "Aucun élément ne correspond à ces filtres.",
  action,
  icon = Inbox,
  inline = true,
}) {
  return <State tone="" icon={icon} title={title} text={text} actions={action} inline={inline} testid="state-empty" />;
}

export function ErrorState({ error, title = "Le chargement a échoué", onRetry, retryLabel = "Réessayer", inline = true }) {
  const retryAfter = error?.retryAfter;
  const text = error?.message
    || "L'API d'administration n'a pas répondu. L'incident a peut-être déjà été signalé.";
  return (
    <State
      tone="danger"
      icon={TriangleAlert}
      title={title}
      text={retryAfter ? `${text} Réessayez dans ${retryAfter} s.` : text}
      inline={inline}
      testid="state-error"
      actions={onRetry && (
        <button type="button" className="adm-btn" onClick={onRetry} data-testid="state-retry">
          <RefreshCw size={13} />{retryLabel}
        </button>
      )}
      meta={error?.requestId && <>Identifiant de requête <code>{error.requestId}</code></>}
    />
  );
}

/* Ne révèle jamais la donnée protégée : ni aperçu, ni compteur, ni nom de
   ressource. On indique seulement ce qu'il faut pour obtenir l'accès. */
export function PermissionDenied({
  requiredRole,
  permission,
  contact = "un administrateur de la console",
  onRequest,
  inline = true,
}) {
  const parts = [];
  if (requiredRole) parts.push(`le rôle « ${requiredRole} »`);
  if (permission) parts.push(`la permission « ${permission} »`);
  const detail = parts.length ? `Cette section exige ${parts.join(" et ")}.` : "Cette section exige des droits que votre compte n'a pas.";
  return (
    <State
      tone="warn"
      icon={Lock}
      title="Accès refusé"
      text={`${detail} Demandez-les à ${contact} si votre travail l'exige.`}
      inline={inline}
      testid="state-denied"
      actions={onRequest && (
        <button type="button" className="adm-btn" onClick={onRequest} data-testid="state-request-access">
          Demander l'accès
        </button>
      )}
    />
  );
}

/* Muet sur l'existence de la ressource : « introuvable » et « interdit »
   renvoient volontairement le même écran, pour ne pas servir d'oracle. */
export function NotFound({ title = "Page indisponible", action, inline = true }) {
  return (
    <State
      tone=""
      icon={FileQuestion}
      title={title}
      text="Cette adresse ne mène à rien d'affichable pour votre compte. Vérifiez le lien, ou revenez à la liste."
      actions={action}
      inline={inline}
      testid="state-notfound"
    />
  );
}

export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

// Ne s'affiche que hors connexion, sauf `force` (galerie de démonstration).
export function Offline({ force = false, onRetry, children = null, inline = true }) {
  const online = useOnline();
  if (online && !force) return <>{children}</>;
  return (
    <State
      tone="warn"
      icon={WifiOff}
      title="Vous êtes hors connexion"
      text="Les actions d'administration sont suspendues tant que la connexion n'est pas rétablie. Rien n'a été perdu."
      inline={inline}
      testid="state-offline"
      actions={onRetry && (
        <button type="button" className="adm-btn" onClick={onRetry}><RefreshCw size={13} />Réessayer</button>
      )}
    />
  );
}

export function Maintenance({ until, message, inline = true }) {
  return (
    <State
      tone="warn"
      icon={Wrench}
      title="Maintenance en cours"
      text={message || "La console est en lecture seule pendant une opération de maintenance."}
      inline={inline}
      testid="state-maintenance"
      meta={until && <>Fin estimée : {until}</>}
    />
  );
}

/* État d'honnêteté : la section existe dans la navigation, mais l'objet
   correspondant n'existe pas encore dans le produit. On le dit franchement
   plutôt que d'afficher des chiffres inventés. */
export function NotApplicable({
  title = "Section sans objet pour l'instant",
  text = "Cette section n'a pas encore d'objet : Perform ne facture pas encore.",
  action,
  inline = true,
}) {
  return (
    <State tone="" icon={CircleSlash} title={title} text={text} actions={action} inline={inline} testid="state-na" />
  );
}

export function Success({ title = "C'est fait", text, action, inline = true }) {
  return <State tone="ok" icon={CheckCircle2} title={title} text={text} actions={action} inline={inline} testid="state-success" />;
}
